import type { Post } from '@moussenaires/shared';
import { formatRelativeTime } from '@moussenaires/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MessageCircle, Pencil, Pin, PinOff, Send, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { feedApi } from '../api/feed.js';
import { mediaSrc } from '../api/client.js';
import { useAuth } from '../context/AuthContext.js';
import { Avatar } from './Avatar.js';
import { ReactionBar } from './ReactionBar.js';
import './PostCard.css';

export function PostCard({ post }: { post: Post }) {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const locale = i18n.language as 'fr' | 'en';

  const [commentsOpen, setCommentsOpen] = useState(false);
  const [commentDraft, setCommentDraft] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(post.title);
  const [editBody, setEditBody] = useState(post.body ?? '');

  const isAdmin = user?.role === 'admin';
  const isAuthor = user?.id === post.author.id;
  const canModerate = isAdmin || isAuthor;

  const detailQuery = useQuery({
    queryKey: ['feed', 'post', post.id],
    queryFn: () => feedApi.get(post.id),
    enabled: commentsOpen,
  });

  function invalidateFeed() {
    void queryClient.invalidateQueries({ queryKey: ['feed'] });
  }

  const updateMutation = useMutation({
    mutationFn: () =>
      feedApi.update(post.id, { title: editTitle.trim(), body: editBody.trim() || null }),
    onSuccess: () => {
      invalidateFeed();
      setIsEditing(false);
    },
  });
  const deleteMutation = useMutation({
    mutationFn: () => feedApi.remove(post.id),
    onSuccess: invalidateFeed,
  });
  const pinMutation = useMutation({
    mutationFn: () => feedApi.update(post.id, { isPinned: !post.isPinned }),
    onSuccess: invalidateFeed,
  });
  const commentMutation = useMutation({
    mutationFn: () => feedApi.addComment(post.id, commentDraft.trim()),
    onSuccess: () => {
      setCommentDraft('');
      void queryClient.invalidateQueries({ queryKey: ['feed', 'post', post.id] });
      invalidateFeed();
    },
  });
  const deleteCommentMutation = useMutation({
    mutationFn: (commentId: number) => feedApi.removeComment(commentId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['feed', 'post', post.id] });
      invalidateFeed();
    },
  });

  return (
    <article className={`post-card card ${post.isPinned ? 'post-card-pinned' : ''}`}>
      <header className="post-card-header">
        <Avatar name={post.author.displayName} avatarUrl={post.author.avatarUrl} />
        <div className="post-card-author">
          <span className="post-card-author-name">{post.author.displayName}</span>
          <time className="post-card-date" dateTime={post.createdAt}>
            {formatRelativeTime(post.createdAt, locale)}
          </time>
        </div>
        {post.isPinned && (
          <span className="badge post-card-pin-badge">
            <Pin size={11} /> {t('feed.pinned')}
          </span>
        )}
        <div className="post-card-actions">
          {isAdmin && (
            <button
              type="button"
              className="btn btn-ghost btn-icon"
              onClick={() => pinMutation.mutate()}
              aria-label={t('feed.pin')}
            >
              {post.isPinned ? <PinOff size={15} /> : <Pin size={15} />}
            </button>
          )}
          {canModerate && !isEditing && (
            <button
              type="button"
              className="btn btn-ghost btn-icon"
              onClick={() => setIsEditing(true)}
              aria-label={t('common.edit')}
            >
              <Pencil size={15} />
            </button>
          )}
          {canModerate && (
            <button
              type="button"
              className="btn btn-ghost btn-icon"
              onClick={() => window.confirm(t('feed.deletePostConfirm')) && deleteMutation.mutate()}
              aria-label={t('common.delete')}
            >
              <Trash2 size={15} />
            </button>
          )}
        </div>
      </header>

      {isEditing ? (
        <div className="post-card-edit">
          <input
            value={editTitle}
            onChange={(event) => setEditTitle(event.target.value)}
            maxLength={140}
          />
          <textarea
            rows={3}
            value={editBody}
            onChange={(event) => setEditBody(event.target.value)}
            maxLength={8000}
          />
          <div className="post-card-edit-actions">
            <button type="button" className="btn" onClick={() => setIsEditing(false)}>
              {t('common.cancel')}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={!editTitle.trim()}
              onClick={() => updateMutation.mutate()}
            >
              {t('common.save')}
            </button>
          </div>
        </div>
      ) : (
        <>
          <h2 className="post-card-title">{post.title}</h2>
          {post.body && <p className="post-card-body">{post.body}</p>}
        </>
      )}

      {post.editedByAdmin && (
        <span className="post-card-edited-note">{t('feed.editedByAdmin')}</span>
      )}

      {post.tags.length > 0 && (
        <div className="post-card-tags">
          {post.tags.map((tag) => (
            <span key={tag} className="badge">
              #{tag}
            </span>
          ))}
        </div>
      )}

      {post.attachments.length > 0 && (
        <div className="post-card-attachments">
          {post.attachments.map((attachment) =>
            attachment.kind === 'link' ? (
              <a
                key={attachment.id}
                href={attachment.url}
                target="_blank"
                rel="noreferrer"
                className="post-attachment-link"
              >
                {attachment.title ?? attachment.url}
              </a>
            ) : attachment.kind === 'image' ? (
              <img
                key={attachment.id}
                className="post-attachment-media"
                src={mediaSrc(attachment.url)}
                alt={attachment.title ?? ''}
                loading="lazy"
              />
            ) : attachment.kind === 'video' ? (
              <video
                key={attachment.id}
                className="post-attachment-media"
                src={mediaSrc(attachment.url)}
                controls
                preload="metadata"
              />
            ) : (
              <audio
                key={attachment.id}
                className="post-attachment-audio"
                src={mediaSrc(attachment.url)}
                controls
                preload="metadata"
              />
            ),
          )}
        </div>
      )}

      <ReactionBar postId={post.id} reactions={post.reactions} />

      <button
        type="button"
        className="btn btn-ghost post-card-comment-toggle"
        onClick={() => setCommentsOpen((value) => !value)}
      >
        <MessageCircle size={15} /> {t('feed.commentCount', { count: post.commentCount })}
      </button>

      {commentsOpen && (
        <div className="post-card-comments">
          {detailQuery.data?.comments.map((comment) => (
            <div key={comment.id} className="post-comment">
              <Avatar
                name={comment.author.displayName}
                avatarUrl={comment.author.avatarUrl}
                size={26}
              />
              <div className="post-comment-body">
                <span className="post-comment-author">{comment.author.displayName}</span>
                <p>{comment.body}</p>
                <time dateTime={comment.createdAt}>
                  {formatRelativeTime(comment.createdAt, locale)}
                </time>
              </div>
              {(isAdmin || user?.id === comment.author.id) && (
                <button
                  type="button"
                  className="btn btn-ghost btn-icon"
                  onClick={() => deleteCommentMutation.mutate(comment.id)}
                  aria-label={t('common.delete')}
                >
                  <Trash2 size={13} />
                </button>
              )}
            </div>
          ))}

          <div className="post-comment-form">
            <input
              placeholder={t('feed.addComment')}
              value={commentDraft}
              onChange={(event) => setCommentDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && commentDraft.trim()) commentMutation.mutate();
              }}
              maxLength={4000}
            />
            <button
              type="button"
              className="btn btn-ghost btn-icon"
              disabled={!commentDraft.trim() || commentMutation.isPending}
              onClick={() => commentMutation.mutate()}
              aria-label={t('feed.send')}
            >
              <Send size={15} />
            </button>
          </div>
        </div>
      )}
    </article>
  );
}
